import { request, url } from "@frontend/lib/http";

import type { Category, Contact, ContactRelations } from "../types";
import type { ContactInput } from "./contactInput";

type RequestOptions = { signal?: AbortSignal };

export interface AddressBookApi {
  fetchCategories(
    addressBookName: string,
    options?: RequestOptions,
  ): Promise<Category[]>;
  fetchContacts(
    addressBookName: string,
    categoryType: string,
    categoryKey: string,
    options?: RequestOptions,
  ): Promise<Contact[]>;
  fetchContactsShow(
    contactId: string,
    options?: RequestOptions,
  ): Promise<Contact>;
  fetchContactRelations(
    contactId: number,
    options?: RequestOptions,
  ): Promise<ContactRelations>;
  fetchContactsCreate(
    addressBookName: string,
    data: ContactInput,
    options?: RequestOptions,
  ): Promise<Contact>;
  fetchContactsUpdate(
    contactId: string,
    data: ContactInput,
    options?: RequestOptions,
  ): Promise<Contact>;
  fetchContactsUpdatePhoto(
    contactId: string,
    file: File,
    options?: RequestOptions,
  ): Promise<{ url: string; mimeType: string; updatedAt: string }>;
}

export const addressBookApi: AddressBookApi = {
  async fetchCategories(addressBookName, options) {
    return request<Category[]>({
      route: "api_contacts_categories_counts",
      query: { addressBookName },
      ...options,
    });
  },

  async fetchContacts(addressBookName, categoryType, categoryKey, options) {
    return request<Contact[]>({
      route: "api_contacts_list",
      query: { addressBookName, categoryType, categoryKey },
      ...options,
    });
  },

  async fetchContactsShow(contactId, options) {
    return request<Contact>({
      route: "api_contacts_show",
      params: { contactId },
      ...options,
    });
  },

  async fetchContactRelations(contactId, options) {
    return request<ContactRelations>({
      route: "api_contacts_relations",
      params: { contactId },
      ...options,
    });
  },

  async fetchContactsCreate(addressBookName, data, options) {
    return request<Contact>({
      route: "api_contacts_create",
      query: { addressBookName },
      method: "POST",
      body: { contactInput: data },
      ...options,
    });
  },

  async fetchContactsUpdate(contactId, data, options) {
    return request<Contact>({
      route: "api_contacts_update",
      params: { contactId },
      method: "PUT",
      body: { contactInput: data },
      ...options,
    });
  },

  async fetchContactsUpdatePhoto(contactId, file, options) {
    const response = await fetch(
      url("api_contacts_update_photo", { contactId }),
      {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
        ...options,
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to upload photo: ${response.statusText}`);
    }

    return response.json();
  },
};
